import addDays from "date-fns/addDays";
import addMonths from "date-fns/addMonths";
import addYears from "date-fns/addYears";
import differenceInCalendarDays from "date-fns/differenceInCalendarDays";
import differenceInCalendarMonths from "date-fns/differenceInCalendarMonths";
import differenceInCalendarYears from "date-fns/differenceInCalendarYears";
import endOfDay from "date-fns/endOfDay";
import getHours from "date-fns/getHours";
import getMinutes from "date-fns/getMinutes";
import isAfter from "date-fns/isAfter";
import isBefore from "date-fns/isBefore";
import isEqual from "date-fns/isEqual";
import isSameDay from "date-fns/isSameDay";
import isSameHour from "date-fns/isSameHour";
import isSameMinute from "date-fns/isSameMinute";
import isSameMonth from "date-fns/isSameMonth";
import isWithinInterval from "date-fns/isWithinInterval";
import setHours from "date-fns/setHours";
import setMinutes from "date-fns/setMinutes";
import startOfDay from "date-fns/startOfDay";
import subMonths from "date-fns/subMonths";
import subYears from "date-fns/subYears";
import { DateRange } from "../components/types";

export type InvalidDateType =
  | "INVALID_DUE_TO_MIN_DATE"
  | "INVALID_DUE_TO_MAX_DATE"
  | "INVALID_DUE_TO_INCLUDE_DATES "
  | "INVALID_DUE_TO_EXCLUDE_DATES";

export const isWeekInMonth = (monthDate: Date, startOfWeek: Date): boolean => {
  const endOfWeek = addDays(startOfWeek, 6);
  return isSameMonth(monthDate, startOfWeek) || isSameMonth(monthDate, endOfWeek);
};

export const handleRefs = (...refs: any[]) => {
  return (node: any) => {
    refs.forEach((ref) => {
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    });
  };
};

export const normalizeArrowKey = (event: KeyboardEvent) => {
  const { key, keyCode } = event;
  if (keyCode >= 37 && keyCode <= 40 && key.indexOf("Arrow") !== 0) {
    return `Arrow${key}`;
  }
  return key;
};

export const isValidDate = (
  date: Date,
  minDate: Date,
  maxDate: Date,
  excludeDates: Date[],
  includeDates: Date[]
): InvalidDateType | true => {
  if (minDate && differenceInCalendarDays(date, minDate) < 0) {
    return "INVALID_DUE_TO_MIN_DATE";
  }
  if (maxDate && differenceInCalendarDays(date, maxDate) > 0) {
    return "INVALID_DUE_TO_MAX_DATE";
  }

  if (includeDates && !includeDates.some((includeDate) => isSameDay(date, includeDate))) {
    return "INVALID_DUE_TO_INCLUDE_DATES ";
  }
  if (excludeDates && excludeDates.some((excludeDate) => isSameDay(date, excludeDate))) {
    return "INVALID_DUE_TO_EXCLUDE_DATES";
  }
  return true;
};

export const isDayDisabled = (day: Date, minDate: Date, maxDate: Date, excludeDates: Date[], includeDates: Date[]) => {
  return (
    isOutOfBounds(day, minDate, maxDate) ||
    (excludeDates && excludeDates.some((excludeDate) => isSameDay(day, excludeDate))) ||
    (includeDates && !includeDates.some((includeDate) => isSameDay(day, includeDate))) ||
    false
  );
};

export const isOutOfBounds = (day: Date, minDate: Date, maxDate: Date) => {
  return (
    (minDate && differenceInCalendarDays(day, minDate) < 0) || (maxDate && differenceInCalendarDays(day, maxDate) > 0)
  );
};

export const monthDisabledBefore = (day: Date, minDate: Date, includeDates: Date[]) => {
  const previousMonth = subMonths(day, 1);
  return (
    (minDate && differenceInCalendarMonths(minDate, previousMonth) > 0) ||
    (includeDates && includeDates.every((includeDate) => differenceInCalendarMonths(includeDate, previousMonth) > 0)) ||
    false
  );
};

export const monthDisabledAfter = (day: Date, maxDate: Date, includeDates: Date[]) => {
  const nextMonth = addMonths(day, 1);
  return (
    (maxDate && differenceInCalendarMonths(nextMonth, maxDate) > 0) ||
    (includeDates && includeDates.every((includeDate) => differenceInCalendarMonths(nextMonth, includeDate) > 0)) ||
    false
  );
};

export const yearDisabledBefore = (day: Date, minDate: Date, includeDates: Date[]) => {
  const previousYear = subYears(day, 1);
  return (
    (minDate && differenceInCalendarYears(minDate, previousYear) > 0) ||
    (includeDates && includeDates.every((includeDate) => differenceInCalendarYears(includeDate, previousYear) > 0)) ||
    false
  );
};

export const yearDisabledAfter = (day: Date, maxDate: Date, includeDates: Date[]) => {
  const nextYear = addYears(day, 1);
  return (
    (maxDate && differenceInCalendarYears(nextYear, maxDate) > 0) ||
    (includeDates && includeDates.every((includeDate) => differenceInCalendarYears(nextYear, includeDate) > 0)) ||
    false
  );
};

export const isDayInRange = (day: Date, startDate: Date, endDate: Date) => {
  let valid;
  const start = startOfDay(startDate);
  const end = endOfDay(endDate);

  try {
    valid = isWithinInterval(day, { start, end });
  } catch (err) {
    valid = false;
  }
  return valid;
};

export const getRangeInformation = (
  day: Date,
  rangeHoverDay: Date | null,
  startDate: Date,
  endDate: Date,
  selectsStart: boolean,
  selectsEnd: boolean,
  isDisabled: boolean
): DateRange => {
  const range: DateRange = {
    rangeStart: false,
    rangeEnd: false,
    inRange: false,
    selectionRangeStart: false,
    selectionRangeEnd: false,
    selectionInRange: false,
  };
  const selectionInRange = !(
    isDisabled ||
    !(selectsStart || selectsEnd) ||
    !rangeHoverDay ||
    !isInSelectionRange(selectsStart, selectsEnd, day, rangeHoverDay, startDate, endDate)
  );
  range.selectionInRange = selectionInRange;
  if (startDate) {
    range.rangeStart = isSameDay(startDate, day);
  }
  if (endDate) {
    range.rangeEnd = isSameDay(endDate, day);
  }
  if (startDate && endDate) {
    range.inRange = isDayInRange(day, startDate, endDate);
  }

  if (selectsStart && rangeHoverDay) {
    range.selectionRangeStart = selectionInRange && isSameDay(day, rangeHoverDay);
  }
  if (selectsEnd && rangeHoverDay) {
    range.selectionRangeEnd = selectionInRange && isSameDay(day, rangeHoverDay);
  }
  return range;
};

export const isInSelectionRange = (
  selectsStart: boolean,
  selectsEnd: boolean,
  day: Date,
  rangeHoverDay: Date,
  startDate: Date,
  endDate: Date
): boolean => {
  if (selectsStart && endDate && (isBefore(rangeHoverDay, endDate) || isEqual(rangeHoverDay, endDate))) {
    return isDayInRange(day, rangeHoverDay, endDate);
  }

  if (selectsEnd && startDate && (isAfter(rangeHoverDay, startDate) || isEqual(rangeHoverDay, startDate))) {
    return isDayInRange(day, startDate, rangeHoverDay);
  }
  return false;
};

export const isSameTime = (timeLeft: Date, timeRight: Date): boolean => {
  return timeLeft && timeRight && isSameMinute(timeLeft, timeRight) && isSameHour(timeLeft, timeRight);
};

export const isWithinTime = (currentTime: Date, minTime: Date, maxTime: Date): boolean => {
  const baseDate = new Date();
  const curr = setHours(setMinutes(baseDate, getMinutes(currentTime)), getHours(currentTime));
  const min = setHours(setMinutes(baseDate, getMinutes(minTime)), getHours(minTime));
  const max = setHours(setMinutes(baseDate, getMinutes(maxTime)), getHours(maxTime));
  return isWithinInterval(curr, {
    start: min,
    end: max,
  });
};

export const getPreviousMonth = (date: Date, step: number) => subMonths(date, step);

export const getNextMonth = (date: Date, step: number) => addMonths(date, step);

export const getPreviousYear = (date: Date) => subYears(date, 1);

export const getNextYear = (date: Date) => addYears(date, 1);
